import CheckCircleIcon from "@/assets/check-circle-icon.svg?react"
import CloseIcon from "@/assets/close-icon.svg?react"
import CommentIcon from "@/assets/comment-icon.svg?react"
import ThumbUpIcon from "@/assets/thumb-up-icon.svg?react"
import ThumbDownIcon from "@/assets/thumb-down-icon.svg?react"
import { useAuthStore } from "@/entities/auth/auth.model"
import { useSendFeedbackMutation } from "@/entities/translation/translation.queries"
import { FeedbackForm } from "@/entities/translation/translation.types"
import { Button, Textarea, cn, debounce } from "@beringlab/ui"
import { useRouterState } from "@tanstack/react-router"
import { format } from "date-fns"
import { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

const FeedbackDialog = () => {
  const { t } = useTranslation()
  const router = useRouterState()

  const { user } = useAuthStore()
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const [isFeedbackSent, setIsFeedbackSent] = useState<boolean>(false)
  const [feedbackForm, setFeedbackForm] = useState<FeedbackForm>({
    liked: undefined,
    reportingUrl: router.location.pathname,
    content: null,
    reportedAt: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
    userId: user?.id,
  })

  const [isFloatingButtonClicked, setIsFloatingButtonClicked] =
    useState<boolean>(false)

  const { mutate: sendFeedback, isPending } = useSendFeedbackMutation({
    onSuccess: () => {
      setIsFeedbackSent(true)
      if (textAreaRef.current) {
        textAreaRef.current.value = ""
        textAreaRef.current.style.height = "auto"
      }
      setFeedbackForm({
        reportingUrl: router.location.pathname,
        content: null,
        reportedAt: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
        userId: user?.id,
      })
    },
  })

  const debouncedSetFeedbackForm = useMemo(
    () =>
      debounce((value: string) => {
        setFeedbackForm((currentForm) => ({ ...currentForm, content: value }))
      }, 500),
    []
  )

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target
    textarea.style.height = "auto"
    textarea.style.height = `${textarea.scrollHeight}px`
    debouncedSetFeedbackForm(e.target.value)
  }

  const handleSendFeedback = async () => {
    sendFeedback({ data: feedbackForm })
  }

  return (
    <div className="fixed right-0 top-[66%] z-40 -translate-y-1/2 items-stretch">
      <div className="flex overflow-auto">
        <Button
          variant="outline"
          className={cn(
            "absolute text-default",
            isFloatingButtonClicked
              ? "-left-[15px] top-[310px]"
              : "-left-3 top-[310px]",
            "origin-right -translate-x-full -rotate-90 transform whitespace-nowrap rounded-b-none border-b-transparent py-[13px]"
          )}
          onClick={() => {
            setIsFloatingButtonClicked(!isFloatingButtonClicked)
            if (isFloatingButtonClicked) {
              setFeedbackForm({
                reportingUrl: router.location.pathname,
                content: null,
                reportedAt: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
                userId: user?.id,
              })
            }
          }}
          size="withIconLg"
        >
          <section className="flex items-center space-x-2">
            {isFloatingButtonClicked ? (
              <CloseIcon fill="#222222" />
            ) : (
              <CommentIcon fill="#222222" />
            )}
            <span>Feedback</span>
          </section>
        </Button>
        <section
          className={cn(
            "h-[444px] rounded-default rounded-bl-none bg-card p-4 shadow-md",
            isFeedbackSent && "relative"
          )}
          style={{
            border: isFloatingButtonClicked ? "1px solid #dfdee2" : "none",
            width: isFloatingButtonClicked ? "380px" : 0,
            padding: isFloatingButtonClicked ? "36px 32px" : 0,
          }}
        >
          {isFeedbackSent && (
            <div className="absolute left-0 right-0 top-0 h-1 overflow-hidden rounded-t-default bg-gray-200">
              <div
                className="animate-progress-countdown h-full bg-[#0FBF81]"
                onAnimationEnd={() => {
                  // 애니메이션이 끝나면 자동으로 피드백 다이얼로그 닫기
                  setIsFeedbackSent(false)
                  setIsFloatingButtonClicked(false)
                }}
              />
            </div>
          )}

          {isFeedbackSent ? (
            <section className="mt-[60px] h-full">
              <section className="mb-4 flex justify-center">
                <CheckCircleIcon />
              </section>
              <section className="flex flex-col space-y-2 text-center">
                <h3 className="text-lg font-bold">
                  {t("mt.feedbackSentTitle")}
                </h3>
                <p className="text-sm text-gray-backdrop-900">
                  {t("mt.feedbackSentDescription")}
                </p>
              </section>
            </section>
          ) : (
            <section>
              <div className="space-y-9">
                <h3 className="text-lg font-bold">{t("mt.feedbackTitle")}</h3>
                <section className="flex items-center justify-center space-x-[18px]">
                  <section
                    className="flex flex-col items-center space-y-3 text-gray-backdrop-900"
                    onClick={() => {
                      setFeedbackForm({
                        ...feedbackForm,
                        liked: feedbackForm.liked === true ? undefined : true,
                      })
                    }}
                  >
                    <Button
                      variant="outline"
                      size="iconLg"
                      className={`${feedbackForm.liked ? "border border-key-color" : ""} p-3`}
                    >
                      <ThumbUpIcon
                        width="32"
                        height="32"
                        className={
                          feedbackForm.liked
                            ? "text-key-color"
                            : "text-gray-900"
                        }
                      />
                    </Button>
                  </section>
                  <section
                    className="flex flex-col items-center space-y-3 text-gray-backdrop-900"
                    onClick={() => {
                      setFeedbackForm({
                        ...feedbackForm,
                        liked: feedbackForm.liked === false ? undefined : false,
                      })
                    }}
                  >
                    <Button
                      variant="outline"
                      size="iconLg"
                      className={`${feedbackForm.liked === false ? "border border-key-color" : ""} p-3`}
                    >
                      <ThumbDownIcon
                        width="32"
                        height="32"
                        className={cn(
                          feedbackForm.liked === false
                            ? "text-key-color"
                            : "text-gray-900"
                        )}
                      />
                    </Button>
                  </section>
                </section>
                <p className="text-sm">
                  {feedbackForm.liked == undefined
                    ? null
                    : feedbackForm.liked
                      ? t("mt.feedbackLiked")
                      : t("mt.feedbackNotLiked")}
                </p>
              </div>
              <div className="relative mt-3 space-y-4">
                {feedbackForm.liked !== undefined ? (
                  <div className="relative">
                    <Textarea
                      ref={textAreaRef}
                      onChange={handleChange}
                      className="min-h-[180px] resize-none border border-border-color"
                      placeholder={t("mt.feedbackPlaceholder")}
                    />
                    <Button
                      onClick={handleSendFeedback}
                      disabled={
                        isPending ||
                        (feedbackForm.liked === undefined &&
                          [null, ""].includes(feedbackForm.content))
                      }
                      className="absolute bottom-2 right-2"
                    >
                      {t("mt.feedbackSend")}
                    </Button>
                  </div>
                ) : (
                  <section className="min-h-[212px]" />
                )}
              </div>
            </section>
          )}
        </section>
      </div>
    </div>
  )
}

export default FeedbackDialog
